import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpEventType, HttpResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, skip } from 'rxjs/operators';
import { combine, appendQuery, arrayToQuery } from 'src/app/shared/utilities/route';
import { DocumentPageBase } from 'src/app/shared/models/document-page';
import { APP_CONFIG, Settings } from 'src/app/config/settings';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import { WINDOW } from 'src/app/core/core.module';
import { isEmpty } from 'src/app/shared/utilities/obj-utilities';
import { GlobalWorkerOptions } from 'pdfjs-dist';
import { NamedDocumentFile } from './models/downloaded-file.model';

@Injectable({
    providedIn: 'root'
})
export class DocumentService {
    private readonly coreApiPath: string;

    constructor(
        private readonly http: HttpClient,
        private readonly toasterService: ToasterService,
        @Inject(WINDOW) private readonly window: Window,
        @Inject(APP_CONFIG) private readonly settings: Settings
    ) {
        this.coreApiPath = combine(settings.coreAPIUrl, 'ayanova', 'documents');

        GlobalWorkerOptions.workerSrc = 'assets/pdfjs/pdf.worker.min.mjs';
    }

    saveAs(file: File): void {
        const downloadLink = document.createElement('a');
        const url = URL.createObjectURL(file);
        downloadLink.href = url;
        downloadLink.download = file.name;
        downloadLink.dispatchEvent(new MouseEvent('click'));
    }

    downloadDocument(id: number, fileName?: string): void {
        const doc = this.getDocument(id);
        this.internalDownloadFile(doc, fileName || `${id}.pdf`);
    }

    downloadDocumentUrl(url: string, fileName: string): void {
        const doc = this.internalGetFile(url);
        this.internalDownloadFile(doc, fileName);
    }

    downloadPartialDocument(id: number, pageNumbers: number[]): void {
        const baseUrl = combine(this.coreApiPath, id.toString());
        const url = appendQuery(baseUrl, arrayToQuery('pageNumbers', pageNumbers));
        const doc = this.internalGetFile(url);

        this.internalDownloadFile(doc, `pages-${pageNumbers[0]}-${pageNumbers[pageNumbers.length - 1]}`);
    }

    getFileFromContentDisposition(response: HttpResponse<Blob>): File {
        let filename = 'Unknown';
        const disposition = response.headers.get('Content-Disposition');
        if (disposition && disposition.indexOf('attachment') !== -1) {
            const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
            const matches = filenameRegex.exec(disposition);
            if (!isEmpty(matches) && matches[1]) {
                filename = matches[1].replace(/['"]/g, '');
            }
        }

        const contentType = response.headers.get('Content-Type');
        const blob = new Blob([response.body as unknown as BlobPart], { type: contentType });
        const file = new File([blob], filename, { type: 'data:' + contentType });

        return file;
    }

    getDocumentFile(url: string): Observable<Blob> {
        return this.http.get<any>(url, { responseType: 'blob' as 'json' }).pipe(
            map((response) => {
                const dataType = response.type;

                const binaryData = [];
                binaryData.push(response);

                return new Blob(binaryData, { type: dataType });
            })
        );
    }

    getDocumentFileWithName(url: string): Observable<NamedDocumentFile> {
        return this.http
            .get(url, {
                observe: 'response',
                responseType: 'blob'
            })
            .pipe(
                map((response) => {
                    const name = response.headers.get('file-name')?.replaceAll('"', '');

                    return {
                        file: response.body,
                        name
                    };
                })
            );
    }

    getFile(path: string): Observable<{ file: Blob; url: string }> {
        if (path.indexOf('/') >= 0 || path.indexOf('\\') >= 0) {
            path = btoa(path);
        }

        return this.internalGetFile(combine(this.coreApiPath, path));
    }

    getDocument(id: number): Observable<{ file: Blob; url: string }> {
        const url = combine(this.coreApiPath, id.toString());
        return this.internalGetFile(url);
    }

    getDocumentAsPdf(id: number): Observable<Uint8Array> {
        return this.http
            .get(`${this.coreApiPath}/${id}`, { responseType: 'arraybuffer' })
            .pipe(map((response: ArrayBuffer) => this.getDocumentFromArrayBuffer(response)));
    }

    getDocumentWithDownloadProgress(id: number): Observable<any> {
        return this.http
            .get(`${this.coreApiPath}/${id}`, {
                responseType: 'arraybuffer',
                reportProgress: true,
                observe: 'events'
            })
            .pipe(
                skip(1),
                map((response) => {
                    const data = {
                        percent: 0,
                        body: undefined
                    };

                    if (response.type === HttpEventType.DownloadProgress) {
                        data.percent = Math.round(100 * (response.loaded / response.total));
                    }

                    if (response.type === HttpEventType.Response) {
                        data.percent = 100;
                        data.body = response.body;
                    }

                    return data;
                })
            );
    }

    getDocumentFromArrayBuffer(buffer: ArrayBuffer) {
        return new Uint8Array(buffer);
    }

    getDocumentByUrlAsPdf(url: string): Observable<any> {
        return this.http.get(url, { responseType: 'arraybuffer' });
    }

    internalDownloadFile(value: Observable<{ file: Blob; url: string }>, fileName: string): void {
        value.subscribe((file) => {
            const downloadLink = this.window.document.createElement('a');
            downloadLink.download = fileName;
            downloadLink.href = file.url;
            downloadLink.click();
            this.toasterService.toast('Downloaded successfully!');
        });
    }

    internalGetFile(url: string): Observable<{ file: Blob; url: string }> {
        return this.http.get(url, { responseType: 'blob' }).pipe(
            map((file) => {
                const blobUrl = this.window.window.URL.createObjectURL(file);
                return { file, url: blobUrl };
            })
        );
    }

    openDocument(
        documentId: number,
        windowId: string,
        windowPositionTop?: number,
        windowPositionLeft?: number
    ): Observable<Window> {
        // Generate a random handle ID to guarantee a new window opens
        const windowHandleId = `${windowId}_${this.getRandomArbitrary()}`;
        return this.getDocument(documentId).pipe(
            map((file) => {
                const viewerUrl = `/assets/document-viewer.html?path=${encodeURIComponent(file.url)}`;
                return this.window.open(
                    viewerUrl,
                    windowHandleId,
                    `height=${this.window.outerHeight * 0.8},width=${this.window.outerWidth * 0.5},
                    top=${windowPositionTop ? windowPositionTop : 0},left=${
                        windowPositionLeft ? windowPositionLeft : 0
                    }`
                );
            })
        );
    }

    openDocumentUrl(
        url: string,
        windowId: string,
        windowPositionTop?: number,
        windowPositionLeft?: number
    ): Observable<Window> {
        // Generate a random handle ID to guarantee a new window opens
        const windowHandleId = `${windowId}_${this.getRandomArbitrary()}`;
        return this.internalGetFile(url).pipe(
            map((file) => {
                const viewerUrl = this.getDocumentViewerUrl(file.url);
                return this.window.open(
                    viewerUrl,
                    windowHandleId,
                    `height=${this.window.outerHeight * 0.8},width=${this.window.outerWidth * 0.5},
                    top=${windowPositionTop ? windowPositionTop : 0},left=${
                        windowPositionLeft ? windowPositionLeft : 0
                    }`
                );
            })
        );
    }

    openDocumentUrlInAnotherTab(url: string): Observable<Window> {
        return this.internalGetFile(url).pipe(
            map((file) => {
                const viewerUrl = this.getDocumentViewerUrl(file.url);
                return this.window.open(viewerUrl, '_blank');
            })
        );
    }

    openDocumentFromArrayBuffer(fileArry: Uint8Array | File, windowId: number): Observable<Window> {
        const windowHandleId = `${windowId}_${this.getRandomArbitrary()}`;
        const blobby = new Blob([fileArry], {
            type: 'application/pdf'
        });
        const url = URL.createObjectURL(blobby);
        const viewerUrl = this.getDocumentViewerUrl(url);
        return of(
            this.window.open(
                viewerUrl,
                windowHandleId,
                `height=${this.window.outerHeight * 0.8},width=${this.window.outerWidth * 0.5},top=${0},left=${0}`
            )
        );
    }

    openDocumentFromBlob(
        fileBlob: Blob,
        windowId: string,
        windowPositionTop?: number,
        windowPositionLeft?: number
    ): Observable<Window> {
        const windowHandleId = `${windowId}_${this.getRandomArbitrary()}`;
        const url = URL.createObjectURL(fileBlob);
        const viewerUrl = this.getDocumentViewerUrl(url);
        return of(
            this.window.open(
                viewerUrl,
                windowHandleId,
                `height=${this.window.outerHeight * 0.8},width=${this.window.outerWidth * 0.5},
                top=${windowPositionTop ? windowPositionTop : 0},left=${windowPositionLeft ? windowPositionLeft : 0}`
            )
        );
    }

    openDocumentPage(documentId: number, pageNumber: number, windowId: string): Observable<Window> {
        return this.getDocument(documentId).pipe(
            map((file) => {
                const viewerUrl = `/assets/document-viewer.html?pagenum=${pageNumber}$path=${encodeURIComponent(
                    file.url
                )}`;
                return this.window.open(
                    viewerUrl,
                    windowId,
                    `height=${this.window.outerHeight * 0.8},width=${this.window.outerWidth * 0.5}`
                );
            })
        );
    }

    updateDocument(documentId: number, changes: DocumentPageBase[]): Observable<any> {
        return this.http.put(`${this.coreApiPath}/${documentId}`, changes);
    }

    deleteDocument(documentId: number): Observable<any> {
        const url = combine(this.coreApiPath, `/${documentId}`);
        return this.http.delete<any>(url);
    }

    /**
     * Returns a random UUID
     */
    private getRandomArbitrary(): string {
        const crypto = this.window.crypto || this.window.msCrypto;
        return crypto.randomUUID();
    }

    private getDocumentViewerUrl(url: string) {
        return `/assets/document-viewer.html?path=${encodeURIComponent(url)}`;
    }
}
